"use client";

/**
 * catalogVariants dla templatu CMS `default`.
 *
 * Każdy template ma OWN dispatch lambda - wspólne komponenty
 * (`CDescriptivePage`, `CNews`, `CContactForm`, `CUserAccounts`,
 * `CDefault`, `CEmpty`) live w `components/{catalogs,cms/registry}/`.
 *
 * **Precedencja kontenera (po refactor 2026-06-02):**
 *   1. React `areaOverrides` (nieużywane dla katalogów - tylko 1 strefa
 *      `catalogContent`, struktura zachowana dla symetrii z windowVariants).
 *   2. DB `container_symbol` (z `imp_cms_container.symbol`) przekazany jako
 *      `ContainerOverride` z adaptera `CatalogRenderer`.
 *   3. Hardcoded fallback w `defaults[symbol]` (CDefault z h1+card lub CEmpty).
 *
 * Każda lambda używa wzorca:
 *   `const Container = ContainerOverride ?? <hardcoded CDefault/CEmpty>`
 *
 * Dodawanie wpisu: zob. dokumentację w `components/cms/registry/variants.tsx`.
 */

import type {
  CAdvertisementsContent,
  CBlogContent,
  CBrowserPressOnlineContent,
  CCategoryAdvertisementContent,
  CContactFormContent,
  CDescriptivePageContent,
  CEventsCalendarContent,
  CFaqContent,
  CFilesToDownloadContent,
  CInteractiveMapContent,
  CJobOffersContent,
  CNestedCatalogsContent,
  CCompanyDigitalizationContent,
  CServicesContent,
  CNewsContent,
  CPhotoGalleryContent,
  CPollsContent,
  CRealizationsContent,
  CRegisterationToNewslettersContent,
  CSearchContent,
  CShopCategoryContent,
  CUserAccountsContent,
  CVideoGalleryOuterContent,
} from "@/lib/api/cms-catalog";

/* ─── Catalog content components ──────────────────────────────────────── */
import { CAdvertisements } from "@/components/catalogs/CAdvertisements/CAdvertisements";
import { CBlog } from "@/components/catalogs/CBlog/CBlog";
import { CBrowserPressOnline } from "@/components/catalogs/CBrowserPressOnline/CBrowserPressOnline";
import { CCategoryAdvertisement } from "@/components/catalogs/CCategoryAdvertisement/CCategoryAdvertisement";
import { CContactForm } from "@/components/catalogs/CContactForm/CContactForm";
import { CDedicatedContactForm } from "@/components/catalogs/CDedicatedContactForm/CDedicatedContactForm";
import { CDescriptivePage } from "@/components/catalogs/CDescriptivePage/CDescriptivePage";
import { CDedicatedDescriptivePage } from "@/components/catalogs/CDedicatedDescriptivePage/CDedicatedDescriptivePage";
import { CEventsCalendar } from "@/components/catalogs/CEventsCalendar/CEventsCalendar";
import { CFaq } from "@/components/catalogs/CFaq/CFaq";
import { CFilesToDownload } from "@/components/catalogs/CFilesToDownload/CFilesToDownload";
import { CInteractiveMap } from "@/components/catalogs/CInteractiveMap/CInteractiveMap";
import { CJobOffers } from "@/components/catalogs/CJobOffers/CJobOffers";
import { CDedicatedJobOffers, type CDedicatedJobOffersContent } from "@/components/catalogs/CDedicatedJobOffers/CDedicatedJobOffers";
import { CNestedCatalogs } from "@/components/catalogs/CNestedCatalogs/CNestedCatalogs";
import { CCompanyDigitalization } from "@/components/catalogs/CCompanyDigitalization/CCompanyDigitalization";
import { CServices } from "@/components/catalogs/CServices/CServices";
import { CNews } from "@/components/catalogs/CNews/CNews";
import { CPhotoGallery } from "@/components/catalogs/CPhotoGallery/CPhotoGallery";
import { CPolls } from "@/components/catalogs/CPolls/CPolls";
import { CRealizations } from "@/components/catalogs/CRealizations/CRealizations";
import { CRegisterationToNewsletters } from "@/components/catalogs/CRegisterationToNewsletters/CRegisterationToNewsletters";
import { CSearch } from "@/components/catalogs/CSearch/CSearch";
import { CShopCategory } from "@/components/catalogs/CShopCategory/CShopCategory";
import { CUserAccounts } from "@/components/catalogs/CUserAccounts/CUserAccounts";
import { CVideoGalleryOuter } from "@/components/catalogs/CVideoGalleryOuter/CVideoGalleryOuter";

/* ─── Containers (wspólne, w components/cms/registry/) ────────────────── */
import { CDefault } from "../../CDefault";
import { CEmpty } from "../../CEmpty";

import type { CatalogVariants } from "../../types";

export const catalogVariants: CatalogVariants = {
  /**
   * Default renderer per `componentSymbol`. Każdy lambda dispatch używa
   * `ContainerOverride` z DB (`container_symbol` z `IAPI_catalogByPath`)
   * lub hardcoded fallback'u.
   *
   * Konwencja: katalogi treściowe (descriptive page, news, contact form)
   * fallback'ują do `CDefault` (page h1 + card background). Katalogi formularzy
   * z własnymi sekcjami (user accounts z login/register tabs) - `CEmpty`.
   */
  defaults: {
    c_advertisements: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CAdvertisements content={content as CAdvertisementsContent} />
        </Container>
      );
    },
    c_blog: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      //CBlog renderuje wlasny full-bleed centered header dla OBU widokow:
      //  - article view: eyebrow "Wróć do listy" + kategoria + data + big H1 + autor
      //  - list view: eyebrow (nazwa katalogu) + big H1 (name_subtitle) + description
      //Chowamy title/subtitle na Container zeby nie duplikowac naglowka nad sekcja.
      return (
        <Container background={background}>
          <CBlog content={content as CBlogContent} title={title} subtitle={subtitle} />
        </Container>
      );
    },
    c_browser_press_online: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CBrowserPressOnline content={content as CBrowserPressOnlineContent} />
        </Container>
      );
    },
    c_category_advertisement: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CCategoryAdvertisement content={content as CCategoryAdvertisementContent} />
        </Container>
      );
    },
    c_contact_form: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      /* Gdy modern layout aktywny (hero w payloadzie), CContactForm renderuje wlasny full-bleed
         hero - Container dostaje empty title zeby uniknac duplikacji naglowka. */
      const c = content as CContactFormContent;
      const hasHero = c?.hero !== null && c?.hero !== undefined;
      return (
        <Container
          title={hasHero ? undefined : title}
          subtitle={hasHero ? undefined : subtitle}
          background={background}
        >
          <CContactForm content={c} />
        </Container>
      );
    },
    c_dedicated_contact_form: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      const c = content as CContactFormContent;
      const hasHero = c?.hero !== null && c?.hero !== undefined;
      return (
        <Container
          title={hasHero ? undefined : title}
          subtitle={hasHero ? undefined : subtitle}
          background={background}
        >
          <CDedicatedContactForm content={c} />
        </Container>
      );
    },
    c_descriptive_page: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CEmpty;
      return (
        <Container background={background}>
          <CDescriptivePage content={content as CDescriptivePageContent} />
        </Container>
      );
    },
    c_dedicated_descriptive_page: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CEmpty;
      return (
        <Container background={background}>
          <CDedicatedDescriptivePage content={content as CDescriptivePageContent} />
        </Container>
      );
    },
    c_events_calendar: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CEventsCalendar content={content as CEventsCalendarContent} />
        </Container>
      );
    },
    c_faq: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CFaq content={content as CFaqContent} />
        </Container>
      );
    },
    c_files_to_download: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CFilesToDownload content={content as CFilesToDownloadContent} />
        </Container>
      );
    },
    c_interactive_map: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CInteractiveMap content={content as CInteractiveMapContent} />
        </Container>
      );
    },
    c_job_offers: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CJobOffers content={content as CJobOffersContent} />
        </Container>
      );
    },
    c_dedicated_job_offers: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CDedicatedJobOffers content={content as CDedicatedJobOffersContent} />
        </Container>
      );
    },
    c_nested_catalogs: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CNestedCatalogs content={content as CNestedCatalogsContent} />
        </Container>
      );
    },
    /* c_dedicated_company_digitalization - katalog "cyfryzacji firmy": full-bleed hero (eyebrow+H1+opis
       z config) + grid kart z katalogow potomnych (icon + name + card_description + CTA).
       Container z pustymi title/subtitle - hero renderowany wewnatrz komponentu. */
    c_dedicated_company_digitalization: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container background={background}>
          <CCompanyDigitalization content={content as CCompanyDigitalizationContent} />
        </Container>
      );
    },
    /* c_dedicated_services - katalog "uslug": full-bleed hero (eyebrow+H1+opis z config) + sekcje
       per kategoria (m_dictionary c_services_category), kazda z wlasnym grid'em kart.
       Nieprzypisane childy trafiaja do sekcji "Pozostale". */
    c_dedicated_services: ({ content, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container background={background}>
          <CServices content={content as CServicesContent} />
        </Container>
      );
    },
    c_news: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CNews content={content as CNewsContent} />
        </Container>
      );
    },
    c_photo_gallery: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CPhotoGallery content={content as CPhotoGalleryContent} />
        </Container>
      );
    },
    c_polls: ({ content, title, subtitle, background, userSession, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CPolls content={content as CPollsContent} userSession={userSession} />
        </Container>
      );
    },
    c_dedicated_realizations: ({ content, title, subtitle, background, ContainerOverride }) => {
      //CDefault owija w page h1 + card background - CRealizations ma własny hero
      //(header_html z config) więc container renderuje tylko wrapper strony.
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CRealizations content={content as CRealizationsContent} />
        </Container>
      );
    },
    c_registeration_to_newsletters: ({ content, title, subtitle, background, userSession, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CRegisterationToNewsletters
            content={content as CRegisterationToNewslettersContent}
            userSession={userSession}
          />
        </Container>
      );
    },
    c_search: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CSearch content={content as CSearchContent} />
        </Container>
      );
    },
    c_shop_category: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CShopCategory content={content as CShopCategoryContent} />
        </Container>
      );
    },
    c_user_accounts: ({ content, background, userSession, ContainerOverride }) => {
      const Container = ContainerOverride ?? CEmpty;
      return (
        <Container background={background}>
          <CUserAccounts
            content={content as CUserAccountsContent}
            userSession={userSession}
          />
        </Container>
      );
    },
    c_video_gallery_outer: ({ content, title, subtitle, background, ContainerOverride }) => {
      const Container = ContainerOverride ?? CDefault;
      return (
        <Container title={title} subtitle={subtitle} background={background}>
          <CVideoGalleryOuter content={content as CVideoGalleryOuterContent} />
        </Container>
      );
    },
  },

  /**
   * Katalog ma jedną strefę (`catalogContent`) - areaOverrides w praktyce
   * nieużywany. Struktura zachowana dla symetrii z `windowVariants`.
   */
  areaOverrides: {},
};
